import type { FormApi } from "../../components/form/createForm.ts";
import { createForm } from "../../components/form/createForm.ts";
import { Form } from "../../components/form/Form.tsx";
import { array, type InferOutput, object, optional, pipe, string } from "valibot";
import { requireText } from "../../utils/validation/base.ts";
import { MAX_CHARACTERS_SM } from "../../constants.ts";
import { useTranslation } from "../../state/i18n.tsx";
import type { StepFormProps } from "./OnboardingPage.tsx";
import { Dynamic } from "solid-js/web";
import { SingleSelectInput } from "../../components/menu/Select.tsx";
import { MultiSelectInput } from "../../components/menu/MultiSelect.tsx";
import type { SelectOption } from "../../types/Menu.ts";
import { createSignal, Show } from "solid-js";
import { TextInput } from "../../components/inputs/TextInput.tsx";
import { condition } from "../../utils/solidUtils.ts";
import { isNil } from "lodash-es";
import Section from "../Section.tsx";
import PageHeader from "../PageHeader.tsx";
import { useUserState } from "../../state/user.tsx";
import { useCustomerState } from "../../state/customer.tsx";
import { makePersisted } from "@solid-primitives/storage";
import UserService from "../../services/UserService.ts";

const PersonaliseSchema = pipe(
  object({
    referralSource: optional(string()),
    referralSourceOther: optional(string()),
    goals: optional(array(requireText(MAX_CHARACTERS_SM))),
    goalsOther: optional(string()),
  }),
);
export type Personalise = InferOutput<typeof PersonaliseSchema>;

const PersonaliseForm = (props: StepFormProps) => {
  const { t } = useTranslation();
  const user = useUserState();
  const customer = useCustomerState();
  const [personalisation, setPersonalisation] = makePersisted(createSignal<Personalise>(), { name: "personalisation" });
  const [size] = makePersisted(createSignal<string>(), { name: "size" });
  const updateStatusMutation = UserService.updateStatus();

  const toTextOption = <T,>(option: Omit<SelectOption<T>, "variant">): SelectOption<T> => ({
    ...option,
    variant: "text",
  });

  const REFERRAL_SOURCES: SelectOption<string>[] = [
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_FRIEND_COLLEAGUE), value: "FRIEND_COLLEAGUE" },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_USED_TO_WORK_WITH_MOREAPP),
      value: "USED_TO_WORK_WITH_MOREAPP",
    },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_SEARCH_ENGINE), value: "SEARCH_ENGINE" },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_ADVERTISEMENT), value: "ADVERTISEMENT" },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_SOCIAL_MEDIA), value: "SOCIAL_MEDIA" },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_AI_ASSISTANT), value: "AI_ASSISTANT" },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_REVIEW_PLATFORM), value: "REVIEW_PLATFORM" },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_OTHER), value: "OTHER" },
  ].map(toTextOption);

  const GOALS: SelectOption<string>[] = [
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_IMPROVE_DATA_ACCURACY_AND_INSIGHTS),
      value: "IMPROVE_DATA_ACCURACY_AND_INSIGHTS",
    },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_DIGITISE_FORMS_AND_STANDARDISE_WORKFLOWS),
      value: "DIGITISE_FORMS_AND_STANDARDISE_WORKFLOWS",
    },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_SPEED_UP_ACTIONS_TO_ALIGN_TEAMS),
      value: "SPEED_UP_ACTIONS_TO_ALIGN_TEAMS",
    },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_AVOID_MISTAKES_AND_MISSING_DATA),
      value: "AVOID_MISTAKES_AND_MISSING_DATA",
    },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_SYNCHRONISE_SYSTEMS_AUTOMATICALLY),
      value: "SYNCHRONISE_SYSTEMS_AUTOMATICALLY",
    },
    {
      label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_MOVE_AWAY_FROM_OUTDATED_TOOLS),
      value: "MOVE_AWAY_FROM_OUTDATED_TOOLS",
    },
    { label: t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_OTHER), value: "OTHER" },
  ].map(toTextOption);

  const form = createForm<InferOutput<typeof PersonaliseSchema>>(
    PersonaliseSchema,
    async (value) => {
      setPersonalisation(value);
      await updateStatusMutation
        .mutateAsync({
          finishedTour: true,
          referralSource: value.referralSource,
          referralSourceOther: value.referralSourceOther,
          goals: value.goals,
          goalsOther: value.goalsOther,
          customerId: customer.customerId,
          size: size()!,
        })
        .then(() => {
          localStorage.removeItem("personalisation");
          localStorage.removeItem("size");
          props.next();
        });
    },
    personalisation(),
  );

  return (
    <>
      <PageHeader
        title={t(($) => $.ONBOARDING_PAGE_PERSONALISE_WELCOME, { firstName: user().firstName })}
        subTitle={t(($) => $.ONBOARDING_PAGE_PERSONALISE_SETUP_ACCOUNT)}
        user={user()}
      />
      <Section fullHeight>
        <h2 class="mb-6 text-header-xl text-primary">{t(($) => $.ONBOARDING_PAGE_PERSONALISE_TITLE)}</h2>
        <Form form={form} class="flex size-full grow flex-col">
          {({ value, error, updateValue }) => (
            <div class="flex h-full grow flex-col">
              <div class="flex w-full flex-col gap-y-6">
                <SingleSelectInput<string>
                  label={t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE)}
                  options={REFERRAL_SOURCES}
                  value={value("referralSource")}
                  errorMessage={error("referralSource")}
                  onSelect={(value) => updateValue("referralSource", value)}
                  onClear={() => updateValue("referralSource", undefined)}
                  sameWidth
                />
                <Show when={value("referralSource") === "OTHER"}>
                  <TextInput
                    label={t(($) => $.ONBOARDING_PAGE_PERSONALISE_REFERRAL_SOURCE_ADDITIONAL)}
                    value={value("referralSourceOther")}
                    errorMessage={error("referralSourceOther")}
                    onInput={(e) => updateValue("referralSourceOther", e.target.value)}
                    onBlur={(e) => updateValue("referralSourceOther", e.target.value)}
                  />
                </Show>
                <MultiSelectInput<string>
                  label={t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOAL)}
                  options={GOALS}
                  value={value("goals")}
                  errorMessage={error("goals")}
                  onSelect={(value) => updateValue("goals", value)}
                  sameWidth
                />
                <Show when={condition(value("goals"), (goals) => !isNil(goals) && goals.includes("OTHER"))}>
                  <TextInput
                    label={t(($) => $.ONBOARDING_PAGE_PERSONALISE_GOALS_ADDITIONAL)}
                    value={value("goalsOther")}
                    errorMessage={error("goalsOther")}
                    onInput={(e) => updateValue("goalsOther", e.target.value)}
                    onBlur={(e) => updateValue("goalsOther", e.target.value)}
                  />
                </Show>
              </div>
              <Dynamic component={props.navigation} form={form as unknown as FormApi<never>} back={props.back} />
            </div>
          )}
        </Form>
      </Section>
    </>
  );
};

export default PersonaliseForm;
